import React from 'react';
import { Card, CardContent, Typography, Chip, Box, IconButton } from '@mui/material';
import { Phone, Email, ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  ClientRef?: string;
  referralCategory: 'direct' | 'organisation';
  referralType: string;
  organisationDisplayName?: string;
  referralTypeDisplayName?: string;
  assignedCounsellorName?: string;
  assessed?: boolean;
  onboarded?: boolean;
  termsAccepted?: boolean;
}

interface ClientCardProps {
  client: Client;
  getClientStatus: (client: Client) => string;
  getClientStatusChipStyles: (client: Client) => any;
}

const ClientCard: React.FC<ClientCardProps> = ({ client, getClientStatus, getClientStatusChipStyles }) => {
  const navigate = useNavigate();

  const handleCall = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.location.href = `tel:${client.phone}`;
  };

  const handleEmail = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (client.email) {
      window.location.href = `mailto:${client.email}`;
    }
  };

  const handleCardClick = () => {
    navigate(`/client-details/${client._id}`);
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        mb: 2,
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(102, 126, 234, 0.3)',
        },
        '&:active': {
          transform: 'scale(0.98)',
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontWeight: 600,
                fontSize: '1.1rem',
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {client.firstName} {client.lastName}
            </Typography>
            {client.ClientRef && (
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.75rem',
                  display: 'block'
                }}
              >
                Ref: {client.ClientRef}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              ml: 1
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Status Chip */}
        <Box sx={{ mb: 1.5 }}>
          <Chip
            label={getClientStatus(client)}
            size="small"
            sx={{
              height: '24px',
              borderRadius: '12px',
              fontSize: '0.75rem',
              fontWeight: 600,
              ...getClientStatusChipStyles(client)
            }}
          />
        </Box>

        {/* Info Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1, mb: 1.5 }}>
          {/* Referral Type */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.7rem',
                display: 'block',
                mb: 0.25
              }}
            >
              Referral
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.875rem'
              }}
            >
              {client.referralCategory === 'organisation'
                ? client.organisationDisplayName
                : client.referralTypeDisplayName || 'Direct'}
            </Typography>
          </Box>

          {/* Assigned Counsellor */}
          {client.assignedCounsellorName && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.7rem',
                  display: 'block',
                  mb: 0.25
                }}
              >
                Counsellor
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '0.875rem'
                }}
              >
                {client.assignedCounsellorName}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <IconButton
            size="small"
            onClick={handleCall}
            sx={{
              flex: 1,
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
              border: '1px solid rgba(33, 150, 243, 0.3)',
              borderRadius: 1,
              color: '#2196f3',
              py: 1,
              '&:hover': {
                backgroundColor: 'rgba(33, 150, 243, 0.2)',
              }
            }}
          >
            <Phone sx={{ fontSize: '1.2rem', mr: 0.5 }} />
            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
              Call
            </Typography>
          </IconButton>
          {client.email && (
            <IconButton
              size="small"
              onClick={handleEmail}
              sx={{
                flex: 1,
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                border: '1px solid rgba(76, 175, 80, 0.3)',
                borderRadius: 1,
                color: '#4caf50',
                py: 1,
                '&:hover': {
                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                }
              }}
            >
              <Email sx={{ fontSize: '1.2rem', mr: 0.5 }} />
              <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                Email
              </Typography>
            </IconButton>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ClientCard;
